import { Button, Divider, Grid } from "@material-ui/core";
import { LcmModel } from "../../domain/LcmModel";
import { SectionProps } from "./SectionProps";
import { useFieldArray, Controller } from "react-hook-form";
import React, { useMemo } from "react";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { fieldLabels } from "./fieldlabels";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { aoService } from "../../services";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { Error } from "../common/Error";

export const AoSection: React.FC<{
  sectionProps: SectionProps<LcmModel>;
}> = ({ sectionProps }) => {
  const { control, disabled, errors, lookupTracker } = sectionProps;
  const { token } = useSelector(authSelector);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "awardingOrg",
    keyName: "id",
  });

  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        aoService.search(params || {}, { token }),
    }),
    [token]
  );

  return (
    <Grid container spacing={3}>
      {fields.map((item, index) => {
        const fieldNames = {
          orgId: `awardingOrg[${index}].orgId`,
          contactName: `awardingOrg[${index}].contactName`,
          contactEmail: `awardingOrg[${index}].contactEmail`,
        };
        const itemErrors = errors.orgId?.[index] || {};

        return (
          <React.Fragment key={item.id}>
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <Controller
                control={control}
                name={fieldNames.orgId}
                as={
                  <SearchField
                    searchFieldVariant="single"
                    label={fieldLabels.awardginOrg}
                    datatest="orgId-field"
                    service={mappedOrgLookupService}
                    disabled={disabled}
                    lookupTracker={lookupTracker}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <Controller
                control={control}
                name={fieldNames.contactName}
                as={
                  <TextField
                    label={fieldLabels.awardingOrgContactName}
                    data-test="ao-contact-name-field"
                    fullWidth
                    readOnly={disabled}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <Controller
                control={control}
                name={fieldNames.contactEmail}
                rules={{
                  maxLength: 255,
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: "Entered value does not match an email format",
                  },
                }}
                as={
                  <TextField
                    label={fieldLabels.awardingOrgEmail}
                    data-test="ao-contact-email-field"
                    fullWidth
                    readOnly={disabled}
                  />
                }
              />
              {itemErrors.contactEmail?.type === "pattern" && (
                <Error data-test="email-validation">
                  {/* Type this to avoid pain */}
                  {errors?.awardingOrg?.map((ao: any) => {
                    const emailField = ao.contactEmail.ref;
                    if (emailField.name === fieldNames.contactEmail)
                      return ao.contactEmail.message;
                    return null;
                  })}
                </Error>
              )}
            </Grid>
            <Grid item xs={2}>
              {!disabled ? (
                <Button
                  color="primary"
                  onClick={() => remove(index)}
                  disabled={disabled}
                  data-test="remove-ao"
                >
                  Remove
                </Button>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </React.Fragment>
        );
      })}
      {!disabled ? (
        <Grid item xs={12}>
          <Button
            color="primary"
            onClick={() =>
              append({
                orgId: null,
                contactName: null,
                contactEmail: null,
              })
            }
            disabled={disabled}
            data-test="add-ao"
          >
            Add AO
          </Button>
        </Grid>
      ) : null}
    </Grid>
  );
};
